/**
 * WeeklyCalendar — pure, side-effect-free helpers used by the UI.
 *
 * Mock data generators (getMockStaff / getMockAppointments) intentionally live
 * in `features/appointments/mock-seeds.ts`, NOT here — the presentational
 * `shared/ui/calendar` layer must not contain domain seed data per the Lovable
 * Integration Protocol.
 */
import { addDays, addMinutes, startOfDay, startOfWeek } from 'date-fns';

import type { Appointment, FilterChip } from './types';

export function getMondayOfWeek(d: Date): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

export function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

export function fmtTime(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function parseHHmm(value: string): { h: number; m: number } {
  const [h, m] = value.split(':').map((x) => parseInt(x, 10));
  return { h: h || 0, m: m || 0 };
}

/** Build 30-min slot list between start and end, e.g. "09:00".."19:00" exclusive of end. */
export function buildTimeSlots(start: string, end: string, stepMin = 30): string[] {
  const { h: sh, m: sm } = parseHHmm(start);
  const { h: eh, m: em } = parseHHmm(end);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const out: string[] = [];
  for (let t = startMin; t < endMin; t += stepMin) {
    out.push(`${pad2(Math.floor(t / 60))}:${pad2(t % 60)}`);
  }
  return out;
}

export function getWeekDays(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(startOfDay(weekStart), i));
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Detect overlapping appointments per staff. Returns Set of conflicting appointment ids. */
export function detectConflicts(appointments: Appointment[]): Set<string> {
  const conflicts = new Set<string>();
  const byStaff = new Map<string, Appointment[]>();
  for (const a of appointments) {
    if (a.status === 'cancelled' || a.status === 'no_show') continue;
    if (!byStaff.has(a.staffId)) byStaff.set(a.staffId, []);
    byStaff.get(a.staffId)!.push(a);
  }
  for (const list of byStaff.values()) {
    const sorted = [...list].sort(
      (x, y) => x.startTime.getTime() - y.startTime.getTime(),
    );
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        if (sorted[j].startTime.getTime() >= sorted[i].endTime.getTime()) break;
        conflicts.add(sorted[i].id);
        conflicts.add(sorted[j].id);
      }
    }
  }
  return conflicts;
}

/** Apply active filter chips. AND across types, OR within the same type. */
export function applyFilters(
  appointments: Appointment[],
  filters: FilterChip[],
): Set<string> {
  if (!filters || filters.length === 0) {
    return new Set(appointments.map((a) => a.id));
  }
  const grouped = new Map<string, FilterChip[]>();
  for (const f of filters) {
    if (!grouped.has(f.type)) grouped.set(f.type, []);
    grouped.get(f.type)!.push(f);
  }
  const passing = new Set<string>();
  for (const a of appointments) {
    let ok = true;
    for (const [type, chips] of grouped.entries()) {
      const matches = chips.some((c) => {
        if (type === 'service') return a.serviceCategory === c.value;
        if (type === 'status') return a.status === c.value;
        if (type === 'priority') return c.value === 'vip' ? Boolean(a.isVip) : false;
        return false;
      });
      if (!matches) {
        ok = false;
        break;
      }
    }
    if (ok) passing.add(a.id);
  }
  return passing;
}

export function snapToSlot(date: Date, stepMin = 30): Date {
  const d = new Date(date);
  const minutes = d.getHours() * 60 + d.getMinutes();
  const snapped = Math.round(minutes / stepMin) * stepMin;
  d.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
  return d;
}

export function appointmentDurationMin(a: Appointment): number {
  return Math.max(15, Math.round((a.endTime.getTime() - a.startTime.getTime()) / 60000));
}

export function moveAppointment(
  a: Appointment,
  newDate: Date,
  newStartHHmm: string,
  newStaffId: string,
): Appointment {
  const { h, m } = parseHHmm(newStartHHmm);
  const start = new Date(newDate);
  start.setHours(h, m, 0, 0);
  const end = addMinutes(start, appointmentDurationMin(a));
  return { ...a, startTime: start, endTime: end, staffId: newStaffId };
}
